// App-level toasts for one-off feedback (operation succeeded/failed). Not
// for standing state the user needs to resolve - that's the sticky variant
// below, which never auto-dismisses. Field-level validation stays inline in
// the panel, not here.
//
// The host is mounted once from main.ts, outside the tab content area, so a
// toast survives a tab switch instead of getting torn down with its panel.
import { el } from "./dom";
import { iconCheck, iconAlert, iconClose, iconPin } from "./icons";

export type ToastVariant = "success" | "error";

const SUCCESS_AUTO_DISMISS_MS = 5000;

let hostEl: HTMLElement | null = null;

export function mountToastHost(): HTMLElement {
  if (hostEl) return hostEl;
  hostEl = el("div", { class: "toast-host" }, []);
  return hostEl;
}

export function showToast(message: string, variant: ToastVariant = "success"): void {
  const host = hostEl ?? mountToastHost();

  const dismissBtn = el("button", { type: "button", class: "toast-dismiss", "aria-label": "Dismiss notification" }, [
    iconClose(),
  ]);
  const toast = el(
    "div",
    {
      class: `toast toast-${variant}`,
      // Errors are role="alert" (interrupts, so it's not missed); success
      // is role="status" (announced without interrupting).
      role: variant === "error" ? "alert" : "status",
      "aria-live": variant === "error" ? "assertive" : "polite",
    },
    [
      el("span", { class: "toast-icon" }, [variant === "error" ? iconAlert() : iconCheck()]),
      el("span", { class: "toast-message" }, [message]),
      dismissBtn,
    ],
  );

  function dismiss() {
    toast.remove();
  }
  dismissBtn.addEventListener("click", dismiss);

  host.append(toast);
  // Errors stay until dismissed - too important to risk missing. Success
  // auto-dismisses so routine confirmations don't pile up.
  if (variant === "success") {
    setTimeout(dismiss, SUCCESS_AUTO_DISMISS_MS);
  }
}

// Sticky toasts - for standing risks like "this key isn't backed up yet",
// where auto-dismissing (or vanishing on manual dismiss) could mean someone
// loses a key without noticing the warning disappeared. Keyed by a
// caller-chosen string so re-triggering the same condition updates the
// existing toast instead of stacking duplicates.
const stickyToasts = new Map<string, { toast: HTMLElement; setCollapsed: (c: boolean) => void }>();

export interface StickyToastAction {
  label: string;
  onClick: () => void;
}

export function showStickyToast(key: string, message: string, action?: StickyToastAction): void {
  const host = hostEl ?? mountToastHost();
  const existing = stickyToasts.get(key);
  if (existing) {
    // Condition re-asserted - make sure it's visible, not left collapsed.
    existing.setCollapsed(false);
    return;
  }

  const messageEl = el("span", { class: "toast-message" }, [message]);
  const dismissBtn = el(
    "button",
    { type: "button", class: "toast-dismiss", "aria-label": "Dismiss (the warning stays pinned until resolved)" },
    [iconClose()],
  );
  // Optional action button, rendered before dismiss so the real fix reads
  // as the primary path, not the (x).
  const actionBtn = action
    ? el("button", { type: "button", class: "toast-action-btn" }, [action.label])
    : null;
  if (actionBtn && action) {
    actionBtn.addEventListener("click", action.onClick);
  }
  // Collapsing never makes it fully invisible - it shrinks to a small
  // pinned reminder that reopens on click, so dismissing it can't mean
  // the risk goes unmonitored.
  const collapsedBtn = el(
    "button",
    { type: "button", class: "toast-collapsed-btn", "aria-label": "Warning still active - click to show details" },
    [iconAlert()],
  );

  const toast = el("div", { class: "toast toast-error toast-sticky" }, []);

  let collapsed = false;
  function render() {
    toast.classList.toggle("toast-collapsed", collapsed);
    toast.innerHTML = "";
    if (collapsed) {
      // role=status keeps it out of the way of an in-progress announcement
      // while staying discoverable, rather than going silent.
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      toast.append(collapsedBtn);
    } else {
      toast.setAttribute("role", "alert");
      toast.setAttribute("aria-live", "assertive");
      const children = [
        el("span", { class: "toast-icon" }, [iconAlert()]),
        messageEl,
        el("span", { class: "toast-pin", "aria-hidden": "true", title: "Stays until resolved" }, [iconPin()]),
      ];
      if (actionBtn) children.push(actionBtn);
      children.push(dismissBtn);
      toast.append(...children);
    }
  }
  function setCollapsed(c: boolean) {
    collapsed = c;
    render();
  }

  dismissBtn.addEventListener("click", () => setCollapsed(true));
  collapsedBtn.addEventListener("click", () => setCollapsed(false));
  render();

  host.append(toast);
  stickyToasts.set(key, { toast, setCollapsed });
}

// Call once the underlying condition is actually resolved - the only way a
// sticky toast fully disappears.
export function resolveStickyToast(key: string): void {
  const existing = stickyToasts.get(key);
  if (!existing) return;
  existing.toast.remove();
  stickyToasts.delete(key);
}

// Re-expands collapsed sticky toasts, called on tab navigation so an
// unresolved warning doesn't stay shrunk just because nobody clicked it.
export function reassertStickyToasts(): void {
  for (const { setCollapsed } of stickyToasts.values()) {
    setCollapsed(false);
  }
}
